import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { useDispatch } from 'react-redux';
import { toggleModal } from '../../../reducers/modalReducer.js';
import Certification from "../../../components/Certification";
import SubscriptionPaymentConfirm from '../../../components/modals/SubscriptionPaymentConfirm/SubscriptionPaymentConfirm.jsx';
import UpgradeCards from '../../../components/dashboard/upgradeCards/index.js';

// Helper function to get certification level image
const getCertificationLevelImage = (level) => {
	const images = {
		None: require('../../../../images/dashboard/certifications/noneStar.png'),
		Bronze: require('../../../../images/dashboard/certifications/bronzeStar.png'),
		Silver: require('../../../../images/dashboard/certifications/silverStar.png'),
		Gold: require('../../../../images/dashboard/certifications/goldStar.png'),
	};
	return images[level] || images.None;
};

// Helper function to get certification level display name
const getCertificationLevelName = (cLevel) => {
	return cLevel === "None" || !cLevel ? 'Not Yet Certified' : cLevel;
};

// Helper function to render check icon
const renderCheckIcon = (condition, checkType = 'default') => {
	const checkIconStyle = { width: '24px', height: '24px', marginLeft: '-2px', marginTop: '-2px', left: '12px', top: '4px' };
	const checkIcon = checkType === 'business-audit'
		? require('../../../../images/dashboard/certifications/check1.svg')
		: require('../../../../images/dashboard/certifications/check.svg');

	return condition ? (
		<img src={checkIcon} alt="check" className='pa' style={checkIconStyle} />
	) : (
		<span className="icon round"></span>
	);
};

// Helper function to render score display with NIL logic
const renderScoreDisplay = (value, allowedLevels, companyLevel, label, isRisk = false) => {
	const isAllowed = allowedLevels.some(level => companyLevel?.toLowerCase() === level.toLowerCase());
	const scoreStyle = { backgroundColor: '#02BC7D21', padding: '4px 12px', textAlign: 'center' };
	const nilStyle = { color: '#5E5F66', fontSize: '14px' };

	return (
		<div className='c15 fs1rem br20px' style={scoreStyle}>
			{isAllowed ? (
				<p className={`w100 tal ${isRisk ? 'df aic' : ''}`}>
					<span className={isRisk ? "c9" : "c7"}>{value || 0}%</span>
				</p>
			) : (
				<p className='fsi fw600 w100 tal' style={nilStyle}>NIL</p>
			)}
		</div>
	);
};

// Helper function to render Buy button
const renderBuyButton = (onClick, condition) => {
	if (!condition) return null;

	return (
		<div
			className="bg4 c1 br20px tac df aic jcc"
			style={{ padding: '2px 6px', width: '80px', maxHeight: '32px', fontSize: '14px' }}
			onClick={onClick}
		>
			Buy
		</div>
	);
};

// Configuration for certification levels
const CERTIFICATION_LEVELS = ['None', 'Bronze', 'Silver', 'Gold'];

// Configuration for requirements by level
const REQUIREMENTS_CONFIG = {
	Bronze: {
		image: require('../../../../images/dashboard/certifications/cards/bronze.png'),
		requirements: [
			{
				id: 'p1',
				type: 'data-repository',
				title: 'Data Repository',
				requiredCompleteness: 75,
				moduleKey: 'repository',
				statKey: 'completionPercent',
				allowedLevels: ['bronze', 'silver', 'gold'],
			},
			{
				id: 'p2',
				type: 'business-audit',
				title: 'Business Audit',
				requiredCompleteness: 75,
				requiredRisk: 50,
				moduleKey: `business-audit-{testType}`,
				statKey: 'completionPercent',
				riskKey: 'riskPercent',
				allowedLevels: ['bronze', 'silver', 'gold'],
			},
		],
	},
	Silver: {
		image: require('../../../../images/dashboard/certifications/cards/silver.png'),
		requirements: [
			{
				id: 'p1',
				type: 'data-repository',
				title: 'Data Repository',
				requiredCompleteness: 90,
				moduleKey: 'repository',
				statKey: 'completionPercent',
				allowedLevels: ['silver', 'gold'],
			},
			{
				id: 'p2',
				type: 'business-audit',
				title: 'Business Audit',
				requiredCompleteness: 90,
				requiredRisk: 50,
				moduleKey: `business-audit-{testType}`,
				statKey: 'completionPercent',
				riskKey: 'riskPercent',
				allowedLevels: ['silver', 'gold'],
			},
			{
				id: 'p3',
				type: 'warranty-disclosure',
				title: 'Draft Warranty Disclosures',
				requiredCompleteness: 75,
				moduleKey: 'warranty-disclosure',
				statKey: 'riskPercent',
				allowedLevels: ['silver', 'gold'],
				hasBuyButton: true,
				buyButtonCode: 'DEPF006',
			},
			{
				id: 'p4',
				type: 'directors-questionnaire',
				title: 'Draft Director’s Questionnaires',
				requiredCompleteness: 75,
				requiredRisk: 50,
				moduleKey: `directors-questionnaire-{directorId}`,
				statKey: 'completionPercent',
				riskKey: 'riskPercent',
				allowedLevels: ['silver', 'gold'],
				hasBuyButton: true,
				buyButtonCode: 'DEPF007',
			},
		],
	},
	Gold: {
		image: require('../../../../images/dashboard/certifications/cards/gold.png'),
		requirements: [
			{
				id: 'p1',
				type: 'data-repository',
				title: 'Data Repository',
				requiredCompleteness: 100,
				moduleKey: 'repository',
				statKey: 'completionPercent',
				allowedLevels: ['bronze', 'silver', 'gold'],
			},
			{
				id: 'p2',
				type: 'business-audit',
				title: 'Business Audit',
				requiredCompleteness: 100,
				requiredRisk: 25,
				moduleKey: `business-audit-{testType}`,
				statKey: 'completionPercent',
				riskKey: 'riskPercent',
				allowedLevels: ['bronze', 'silver', 'gold'],
			},
			{
				id: 'p3',
				type: 'warranty-disclosure',
				title: 'Draft Warranty Disclosures',
				requiredCompleteness: 90,
				moduleKey: 'warranty-disclosure',
				statKey: 'completionPercent',
				allowedLevels: ['bronze', 'silver', 'gold'],
				hasBuyButton: true,
				buyButtonCode: 'DEPF006',
			},
			{
				id: 'p4',
				type: 'directors-questionnaire',
				title: 'Draft Director’s Questionnaires',
				requiredCompleteness: 90,
				requiredRisk: 50,
				moduleKey: `directors-questionnaire-{directorId}`,
				statKey: 'completionPercent',
				riskKey: 'riskPercent',
				allowedLevels: ['bronze', 'silver', 'gold'],
				hasBuyButton: true,
				buyButtonCode: 'DEPF007',
			},
		],
	},
};

// Component for rendering a requirement item
const RequirementItem = ({ requirement, level, company, disabledCertificates, directors, dispatch, setShowExpension }) => {
	// Look up stats from certTempModulesStats by certType matching requirement.type
	const certModuleStats = Object.values(company?.['certTempModulesStats'] || {}).find(m => m['certType'] === requirement.type) || {};
	const moduleExists = !!certModuleStats['moduleKey'];

	// Silver warranty-disclosure displays riskPercent as the "completeness" value (statKey: 'riskPercent')
	let completionPercent = 0;
	let riskPercent = 0;
	if (requirement.type === 'warranty-disclosure' && level === 'Silver') {
		completionPercent = certModuleStats['riskPercent'] || 0;
	} else {
		completionPercent = certModuleStats['completionPercent'] || 0;
		riskPercent = certModuleStats['riskPercent'] || 0;
	}

	const isChecked = requirement.type === 'business-audit' || requirement.type === 'directors-questionnaire'
		? completionPercent >= requirement.requiredCompleteness && riskPercent <= requirement.requiredRisk
		: completionPercent >= requirement.requiredCompleteness;

	const allowedLevels = requirement.allowedLevels || [level.toLowerCase()];
	const showScore = allowedLevels.some(l => company['cLevel']?.toLowerCase() === l.toLowerCase());
	const handleBuyClick = () => {
		if (disabledCertificates) return;
		dispatch(toggleModal({ open: 'subscriptionPaymentConfirm' }));
		setShowExpension(requirement.buyButtonCode);
	};

	const shouldShowBuyButton = requirement.hasBuyButton && (disabledCertificates || !moduleExists);
	return (
		<li className={!disabledCertificates && company['points']?.[level]?.[requirement.id] && 'check'}>
			{renderCheckIcon(isChecked, requirement.type === 'business-audit' || requirement.type === 'directors-questionnaire' ? 'business-audit' : 'default')}
			{requirement.type === 'warranty-disclosure' && level === 'Silver' && isChecked && (
				<span className="icon check"><img src={require(`../../../../images/dashboard/certifications/check.svg`)} alt="" /></span>
			)}
			{requirement.type === 'directors-questionnaire' && level === 'Silver' && isChecked && (
				<span className="icon check"><img src={require(`../../../../images/dashboard/certifications/check.svg`)} alt="" /></span>
			)}

			<div className={`df ${requirement.hasBuyButton ? 'jcsb' : ''}`} style={requirement.hasBuyButton ? { columnGap: '1rem' } : {}}>
				<div className='w100'>
					<h4 className='c74 df aic jcsb'>{requirement.title} {renderBuyButton(handleBuyClick, shouldShowBuyButton)}</h4>
					<div className="dg gtcr3">
						<div>	</div>
						<p className='fw300' style={{ color: '#929090', fontSize: '14px' }}>Your Score:</p>
						<p className='fw300' style={{ color: '#929090', fontSize: '14px' }}>Required Score:</p>

						{requirement.type === 'data-repository' || (requirement.type === 'warranty-disclosure' && level === 'Gold') ? (
							<>
								<p className='c15 fs1rem'>
									Completeness
								</p>
								{renderScoreDisplay(
									shouldShowBuyButton ? 0 : completionPercent,
									allowedLevels,
									company['cLevel'],
									'Completeness',
									false
								)}
								<span className="c74">&gt; {requirement.requiredCompleteness}%</span>
							</>
						) : requirement.type === 'warranty-disclosure' && level === 'Silver' ? (
							<>
								<p className='c15 fs1rem'>Completeness</p>
								<div className='c15 fs1rem br20px ' style={{ backgroundColor: '#02BC7D21', padding: '4px 12px', maxWidth: '160px', textAlign: 'center' }}>
									{showScore && !shouldShowBuyButton ? (
										<p className='w100 tal'>{completionPercent}   </p>
									) : (
										<p className='fsi fw600 w100 tal' style={{ color: '#5E5F66', fontSize: '14px' }}>NIL</p>
									)}
								</div>
								<span className="c74">&gt; {requirement.requiredCompleteness || 0}%</span>
							</>
						) : (
							<>
								<p className='df aic w100 c15'>
									Completeness
								</p>
								<div>
									{renderScoreDisplay(
										shouldShowBuyButton ? 0 : completionPercent,
										allowedLevels,
										company['cLevel'],
										'Completeness',
										false
									)}
								</div>

								<span className="c74"> &gt;  {requirement.requiredCompleteness}%</span>
								<p className='df aic w100 c15'>
									Risk
								</p>

								<div className='c15 fs1rem br20px mt0-5rem' style={{ backgroundColor: '#02BC7D21', padding: '4px 12px', maxWidth: '150px', textAlign: 'center' }}>
									{
										showScore && !shouldShowBuyButton ? (
											<p className='df aic w100'><span className="c9">{riskPercent}%</span></p>
										) : (
											<p className='fsi fw600 w100 tal' style={{ color: '#5E5F66', fontSize: '14px' }}>NIL</p>
										)
									}
								</div>
								<span className="c74">&lt; {requirement.requiredRisk}%</span>
							</>
						)}
					</div>
				</div>

			</div>
		</li>
	);
};

// Component for rendering a certification level card
const CertificationLevelCard = ({ level, config, company, disabledCertificates, directors, dispatch, setShowExpension }) => {
	return (
		<div className={`jcsb data-check-box ${disabledCertificates && 'op60'}`} aria-disabled={disabledCertificates}>
			<div className="df aic mb2rem">
				<img src={config.image} alt={`${level} certification`} style={{ width: '82px', height: '100px' }} />
				<div className='df fdc jcfs aifs ml1rem'>
					<h4 className='c15 fs1-5rem lh1 w100 tal'>{level}</h4>
					<span className='c27 fs0-75rem lh1 mt0-5rem'>Unaudited Certification Progress</span>
				</div>
			</div>
			<ul className="data-list">
				{config.requirements.map((requirement) => (
					<RequirementItem
						key={requirement.id}
						requirement={requirement}
						level={level}
						company={company}
						disabledCertificates={disabledCertificates}
						directors={directors}
						dispatch={dispatch}
						setShowExpension={setShowExpension}
					/>
				))}
			</ul>
		</div>
	);
};

export default function Certificates({ company, setTab, directors }) {
	const dispatch = useDispatch();
	let [payload, setPayload] = useState(null);
	let [showExpension, setShowExpension] = useState(null);
	const [linearProgress, setLinearProgress] = useState(25);
	const [disabledCertificates, setDisabledCertificates] = useState(true);

	useEffect(() => {
		if (['Lite', 'Free'].includes(company['subscription'])) setDisabledCertificates(true);
		else setDisabledCertificates(false);

		// Set Linear Progress
		const progressMap = {
			Bronze: 50,
			Silver: 75,
			Gold: 100,
		};
		setLinearProgress(progressMap[company['cLevel']] || 25);
	}, [company]);

	const getDisabledCertificates = (company, level) => {
		if (company['cLevel'] === 'None') {
			return true;
		}
		if (company['cLevel'] === 'Bronze') {
			return level === 'Silver' || level === 'Gold';
		}
		if (company['cLevel'] === 'Silver') {
			return level === 'Gold';
		}
		if (company['cLevel'] === 'Gold') {
			return false;
		}
		return true;
	}

	return (
		<>
			<div className="certificates-page">

				{company['points'] &&
					<>
						{/* {disabledCertificates &&
						<div className="left-upgrade-button">
							<Link className="ass fs1rem pt0-75rem data-check-box" to={`/billing/${company['_id']}`} >
								<h3 className='c6'>* Certification Requires Upgrade To Pro</h3>
							</Link>
						</div>
					} */}
						<div className={disabledCertificates && 'disabled-certificates-page'}>
							<div className="badges-sec c2 br20px df jcsb aife" style={{ backgroundColor: '#2947EF1A', border: '1px solid #7385BE80' }}>
								<div className="pt1rem pl2rem pb1rem df">
									<div className="badges-area w100">
										<div className="certificates-current w100">
											<div className='top-heading'>
												<h2 className='c15'>Your Progress</h2>
												<h4 style={{ color: '#44D991' }}>PRO Only</h4>
												<p className='ass fs0-75rem df aic'>
													<img src={getCertificationLevelImage(company['cLevel'] || 'None')} alt={`${company['cLevel'] || 'None'} star`} />
													<span className='ml0-5rem c15 op70 fsi fs1-25rem w100'>
														{getCertificationLevelName(company['cLevel'])}
													</span>
												</p>
											</div>
											<div className="df ">
												{company['points'] && (
													<span className="packge-select" style={{ width: '100%' }}>
														{CERTIFICATION_LEVELS.map((level) => (
															<div key={level} className={`px1rem ${company['cLevel'] === level && 'current'}`}>
																<Certification
																	className="z12"
																	component='Certificates'
																	level={level}
																	subscription={company['subscription']}
																	expiryTimestamp={company['createdAt']}
																	company={company}
																	large={true}
																/>
															</div>
														))}
													</span>
												)}


											</div>

											<div className='p1rem w100'>
												{company['certificateNext'] &&
													<Box sx={{ width: '100%' }}>
														<div className='certificate-progress-bar certificate-progress-bar-custom'>
															<ProgressBar now={linearProgress} />
														</div>
													</Box>}
											</div>
										</div>
									</div>
								</div>

								<img src={require('../../../../images/dashboard/certifications/cards/certificategirl.png')} alt="certification-bg" className="certification-bg" style={{ maxWidth: '600px', height: 'auto' }} />
							</div>


							{/* Requirements for certification progress */}
							<div className="mt2rem br20px pt2rem pl2rem pb1rem" style={{ backgroundColor: '#fff', border: '1px solid #DBDFEE' }}>
								<div className="progress-sec">
									<div className="df jcsb aifs mb1rem pr2rem">
										<div style={{ paddingLeft: '10px' }}>
											<h2 className='c15'> Requirements for certification progress</h2>
											<h4 style={{ color: '#44D991' }}>PRO Only</h4>
										</div>
										<UpgradeCards company={company} upgradePlan={true} setShowExpension={setShowExpension} />
									</div>
									<div className="container">
										<div className="data-check-sec">
											<div className="dg gg2rem gtcr3">
												{Object.entries(REQUIREMENTS_CONFIG).map(([level, config]) => (
													<CertificationLevelCard
														key={level}
														level={level}
														config={config}
														company={company}
														disabledCertificates={getDisabledCertificates(company, level)}
														directors={directors}
														dispatch={dispatch}
														setShowExpension={setShowExpension}
													/>
												))}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				}
			</div>

			<SubscriptionPaymentConfirm
				plan={!showExpension ? 'Pro' : typeof showExpension === "string" ? showExpension : showExpension?.['moduleKey']}
				setShowExpension={setShowExpension}
				defaultStep={['Lite', 'Pro'].includes(showExpension) ? 'subscriptionUpgrade' : 'modulePurchase'}
				company={company}
				companyId={company['_id']}
				payload={payload}
				module={showExpension}
				showExpension={showExpension}
			/>
		</>
	);
};
